import React, { createContext, useContext, useMemo, useState } from 'react';
import type { Actor } from '@avatar/types';
import {
  StudioProjectVersion,
  TtsVersionSelectOption,
} from '../../../../server/graphql/__generated.types';
import {
  ActiveActorAndVariant,
  TTSVersion,
} from '../../../components/avatar/hooks/useActiveActor';
import { useUserContext } from '../../../context/user/UserContext';

interface StudioToolbarContextProps {
  activeActor: ActiveActorAndVariant;
  setActiveActor: (selectedActor: ActiveActorAndVariant) => void;
  version: TTSVersion;
  setVersion: (version: TTSVersion) => void;
  studioProjectVersion: StudioProjectVersion;
}

const StudioToolbarContext = createContext<StudioToolbarContextProps>(
  // @ts-ignore React.createContext
  {}
);

export const useStudioToolbar = () => {
  return useContext(StudioToolbarContext);
};

export default function StudioToolbarProvider({
  children,
  actor,
  projectVersion,
}: {
  children: any;
  actor: Actor;
  projectVersion: StudioProjectVersion;
}) {
  const { data: userQuery } = useUserContext();

  const [activeActor, setActiveActor] = useState<{
    actor: Actor;
    variant: Actor['variants'][0];
  }>({
    actor,
    variant: actor?.variants[0],
  });

  const [version, setVersion] = useState<TTSVersion>(() => {
    let defaultOption: TtsVersionSelectOption;

    if (projectVersion === StudioProjectVersion.Legacy) {
      defaultOption = userQuery?.user.settings
        ?.defaultTTSVersion as TtsVersionSelectOption;
    } else {
      defaultOption = TtsVersionSelectOption.Latest;
    }

    return {
      label: TtsVersionSelectOption.Latest,
      model: activeActor.variant?.versions[defaultOption]?.model,
    };
  });

  const value = useMemo(
    () => ({
      activeActor,
      setActiveActor,
      version,
      setVersion,
      studioProjectVersion: projectVersion,
    }),
    [activeActor, setActiveActor, version, setVersion, projectVersion]
  );

  return (
    <StudioToolbarContext.Provider value={value}>
      {children}
    </StudioToolbarContext.Provider>
  );
}
